<template>
  <div id="bixe-sidebar-container" class="bixeSidebarContainer" :class="hideClass">
    <div class="sidebar" :class="[extra ? 'split' : 'full', hasNav ? 'hasNav' : 'noNav', extraAnimating ? 'splitAnimating' : '']" ref="sidebar">
      <section id="mainSidebar" class="main">
        <transition name="scale">
          <button v-if="showOnBarClose" class="close" @click="sidebarStore.requestBack">
            <app-icon icon="cross" />
            <span class="visuallyHidden">Close Panel</span>
          </button>
        </transition>
        <transition-group name="flipPanes" @enter="mainEnter" @after-enter="afterPaneEnter" @leave="mainLeave">
          <component
            v-if="mainPane"
            :is="mainPane?.component"
            :key="mainPane"
            v-bind="{ ...mainPane?.props, extra: false }"
            class="mainContent"
            ref="mainContentContainer"
          />
        </transition-group>
        <transition name="bottom-notice">
          <sidebar-notice v-if="hasNotice" key="notices" />
        </transition>
      </section>
      <section class="extra">
        <transition name="basic">
          <div class="divider" v-if="showDivider"></div>
        </transition>
        <button class="close" @click="sidebarStore.clearExtraPane">
          <app-icon icon="cross" />
          <span class="visuallyHidden">Close Panel</span>
        </button>
        <transition-group name="flipPanes" @enter="extraEnter" @after-enter="afterPaneEnter" @leave="extraLeave">
          <component
            v-if="extra"
            :is="extraPane?.component"
            :key="extraPane"
            v-bind="{ ...extraPane?.props, extra: true }"
            class="extraContent"
            ref="extraContentContaner"
          />
        </transition-group>
      </section>
    </div>
    <transition name="navBar">
      <the-sidebar-nav v-if="hasNav" class="sidebarNav" />
    </transition>
  </div>
</template>

<script setup>
import { ref, watch, computed, onBeforeUnmount, getCurrentInstance } from 'vue';
import { watchDebounced, useWindowFocus } from '@vueuse/core';
import _ from 'underscore';
import { storeToRefs } from 'pinia';
import AppIcon from '@/components/AppIcon.vue';
import TheSidebarNav from '@/components/TheSidebarNav.vue';
import OutsideSidebar from '@/components/OutsideSidebar';
import LobbySidebar from '@/components/LobbySidebar';
import WaitingRoomSidebar from '@/components/WaitingRoomSidebar';
import Po3Sidebar from '@/components/Po3Sidebar';
import MeetingTimeSidebar from '@/components/MeetingTimeSidebar';
import MicroskillSidebar from '@/components/MicroskillSidebar';
import EndMeetingSidebar from '@/components/EndMeetingSidebar';
import WhatNextSidebar from '@/components/WhatNextSidebar';
import MicroskillsMenuSidebar from '@/components/MicroskillsMenuSidebar';
import EditTitleSidebar from '@/components/EditTitleSidebar';
import EditPurposeSidebar from '@/components/EditPurposeSidebar';
import EditMeetingInfoListSidebar from '@/components/EditMeetingInfoListSidebar';
import EditProposalSidebar from '@/components/EditProposalSidebar';
import EditTopicSidebar from '@/components/EditTopicSidebar';
import EditCheckInOutQuestionSidebar from '@/components/EditCheckInOutQuestionSidebar';
import EmoteGridSidebar from '@/components/EmoteGridSidebar';
import ParticipantListSidebar from '@/components/ParticipantListSidebar';
import SpacesSidebar from '@/components/SpacesSidebar';
import UserSettingsSidebar from '@/components/UserSettingsSidebar';
import SidebarNotice from '@/components/SidebarNotice';
import { whichTransitionEvent } from '@/resources/css-events';
import { useMeetingStore } from '@/store/pinia/meeting';
import { useMessagesStore } from '@/store/pinia/messages';
import { usePageStore } from '@/store/pinia/page';
import { useSidebarStore } from '@/store/pinia/sidebar';
import { usePhaseStore } from '@/store/pinia/phase';
import { useTimeStore } from '@/store/pinia/time';
import { useFeaturesStore } from '@/store/pinia/features';
import { useUsersStore } from '@/store/pinia/users';
import { useFrameStore } from '@/store/pinia/frame';
import { gsap } from 'gsap';
import { useUiAnimations } from '@/composables/useUiAnimations';
import { useReady } from '@/composables/useReady';
import { useBodyClasses } from '../composables/useBodyClasses';
import screenInfo from '@/resources/screen-info';

const meetingStore = useMeetingStore();
const messagesStore = useMessagesStore();
const pageStore = usePageStore();
const sidebarStore = useSidebarStore();
const phaseStore = usePhaseStore();
const timeStore = useTimeStore();
const featuresStore = useFeaturesStore();
const usersStore = useUsersStore();
const frameStore = useFrameStore();

const focused = useWindowFocus();
const { $ee, $meetingmanager } = getCurrentInstance().appContext.config.globalProperties;
const { animationGuard } = useUiAnimations();
const { removeBodyClass, changeBodyClass } = useBodyClasses();
const { afterMeetingSpaceCard, afterLobbyCard, finishedFirstPhase, isScheduledToFreeformTransition } = useReady();
var ev = whichTransitionEvent();

const focusing = ref(false);
const sidebar = ref(null);
const mainContentContainer = ref(null);
const extraContentContainer = ref(null);

$ee.on('nM:requestFinish', requestFinish);

onBeforeUnmount(() => {
  $ee.off('nM:requestFinish', requestFinish);
});

const { iAmOwner, iAmSpeaking, iAmOverdue, iAmInWaitingRoom, visibleUserCount, posCallTs } = storeToRefs(usersStore);
const { hasNotice } = storeToRefs(messagesStore);
const { noAnimations } = storeToRefs(featuresStore);
const { currentPageName: pageName, isMeeting } = storeToRefs(pageStore);
const { current: currentRaw, previous, extra: extraRaw, previousExtra, isOpen, extraAnimating, forceReady } = storeToRefs(sidebarStore);
const { currentPhase } = storeToRefs(phaseStore);
const { alarmRunning } = storeToRefs(timeStore);
const { fullscreen, isNativeApp } = storeToRefs(frameStore);

const { objectives, outcomes, outputs } = storeToRefs(meetingStore);

const sidebarOpenedThisMeeting = ref(false);
const currentPane = computed(() => {
  if (!forceReady.value && isMeeting.value && !cardsDone.value) {
    return false;
  } else {
    return isScheduledToFreeformTransition.value ? 'lobby' : currentRaw.value.name;
  }
});

const hasNav = computed(
  () =>
    isMeeting.value &&
    currentPane.value &&
    currentPane.value !== 'endBar' &&
    (forceReady.value || (finishedFirstPhase.value && isOpen.value && sidebarOpenedThisMeeting.value)),
);

const cardsDone = computed(() => afterMeetingSpaceCard.value || afterLobbyCard.value);

const extra = computed(() => (isMeeting.value && !cardsDone.value ? false : extraRaw.value.name));
const prevPane = computed(() => currentRaw.value.name);
const panelDirection = computed(() => {
  if (!currentPane.value) {
    return 'toRight';
  }
  if (!prevPane.value) {
    return 'toLeft';
  }

  return currentRaw.value.position > previous.value.position ? 'toLeft' : 'toRight';
});
const extraPanelDirection = computed(() => {
  if (!previousExtra.value.name) {
    return 'toLeft';
  }

  if (!extraRaw.value.name) {
    return 'toRight';
  }

  return extraRaw.value.position > previousExtra.value.position ? 'toLeft' : 'toRight';
});

const hideClass = computed(() => (fullscreen.value && isNativeApp.value ? 'fullscreenHide' : ''));
const capacity = computed(() => {
  if (currentPane.value === 'skillBar') {
    return currentRaw.value.phase === 'breakTime' ? 'misc' : screenInfo[currentRaw.value.phase]?.skill;
  }
  return 'misc';
});

// const sidebarVisible = computed(() => isOpen.value || isAnimating.value);
const absentFromCheckIn = computed(() => {
  return ['checkIn', 'checkOut'].includes(currentPhase.value) && iAmSpeaking.value && currentPane.value !== 'skillBar' && alarmRunning.value;
});

const panes = {
  outside: { component: OutsideSidebar, key: 'outside', props: {} },
  lobby: { component: LobbySidebar, key: 'lobby', props: {} },
  waitingRoom: { component: WaitingRoomSidebar, key: 'waitingRoom', props: {} },
  po3: { component: Po3Sidebar, key: 'po3', props: {} },
  meetingTime: { component: MeetingTimeSidebar, key: 'meetingTime', props: {} },
  skillBar: { component: MicroskillSidebar, key: 'skillBar', props: {} },
  endBar: { component: EndMeetingSidebar, key: 'endBar', props: {} },
  whatNext: { component: WhatNextSidebar, key: 'whatNext', props: {} },
  microskills: { component: MicroskillsMenuSidebar, key: 'microskills', props: {} },
  name: { component: EditTitleSidebar, key: 'editTitle', props: { focusing } },
  purpose: { component: EditPurposeSidebar, key: 'editPurpose', props: {} },
  objectives: {
    component: EditMeetingInfoListSidebar,
    key: 'editObjectives',
    props: { name: 'objectives', title: 'Objectives', field: objectives },
  },
  outcomes: { component: EditMeetingInfoListSidebar, key: 'editOutcomes', props: { name: 'outcomes', title: 'Outcomes', field: outcomes } },
  outputs: { component: EditMeetingInfoListSidebar, key: 'editOutputs', props: { name: 'outputs', title: 'Outputs', field: outputs } },
  checkInQuestion: { component: EditCheckInOutQuestionSidebar, key: 'editCheckInQuestion', props: {} },
  proposal: { component: EditProposalSidebar, key: 'editProposal', props: {} },
  topic: { component: EditTopicSidebar, key: 'editTopic', props: {} },
  settings: { component: UserSettingsSidebar, key: 'settings', props: {} },
  emoteGrid: { component: EmoteGridSidebar, key: 'emotes', props: {} },
  participantList: { component: ParticipantListSidebar, key: 'participantList', props: {} },
  spaces: { component: SpacesSidebar, key: 'spaces', props: {} },
};

const mainPane = computed(() => (currentPane.value ? panes[currentPane.value] : false));
const extraPane = computed(() => (extra.value ? panes[extra.value] : false));

const showOnBarClose = computed(() => {
  if (!isMeeting.value || !currentPane.value) {
    return false;
  } else if (['skillBar', 'lobby', 'endBar'].includes(currentPane.value)) {
    return false;
  } else {
    return currentPane.value !== 'microskills';
  }
});

const showDivider = computed(() => extra.value && capacity.value === 'misc');

watch(noAnimations, (nv) => {
  if (!nv) {
    sidebarStore.setIsEntering(false);
    sidebarStore.setIsLeaving(false);
  }
});

watch(extraPane, (nv, ov) => {
  if (extraRaw.value.isEditPane) {
    $meetingmanager.lockField(extraRaw.value.name);
  }
  if (previousExtra.value.isEditPane) {
    _.delay(() => {
      $meetingmanager.unlockField(previousExtra.value.name);
    }, 500);
  }
  if (!nv || (nv && !ov)) {
    sidebarStore.setExtraAnimating(true);
    _.delay(() => {
      sidebarStore.setExtraAnimating(false);
    }, 500);
  } else {
    sidebarStore.setExtraAnimating(false);
  }
});

watch(currentPane, () => {
  // Handle unlocking
  if (currentRaw.value.isEditPane) {
    $meetingmanager.lockField(currentRaw.value.name);
  }
  if (previous.value.isEditPane) {
    _.delay(() => {
      $meetingmanager.unlockField(previous.value.name);
    }, 500);
  }
});

watch(pageName, (nv) => {
  if (nv !== 'meetingFrame') {
    closeSidebar({ hardClose: true });
    sidebarStore.setForceReady(false);
    sidebarStore.setIsEntering(false);
  }
  sidebarStore.onPageChange(nv);
  sidebarOpenedThisMeeting.value = false;
});

watch(currentPhase, (nv) => {
  cleanUpStyles(mainContentContainer.value);
  if (nv === 'ended') {
    sidebarStore.requestPane(false, { force: true, ending: true }, 'ended');
  }
});

watch(absentFromCheckIn, (nv) => {
  if (nv) {
    messagesStore
      .addAlert('youNeedToEndYourTurn')
      .then(() => {
        $meetingmanager.finish();
      })
      .catch(() => {});
  } else {
    messagesStore.removeAlert({ messageName: 'youNeedToEndYourTurn' });
  }
});

watch(iAmOverdue, (nv) => {
  if (nv) {
    sidebarStore.iAmOverdue();
  }
});

watch(iAmInWaitingRoom, (nv) => {
  if (nv) {
    sidebarStore.iAmInWaitingRoom();
  } else {
    sidebarStore.requestPane(false, { force: false }, 'finishedWaiting');
    setTimeout(sidebarStore.checkForBar, 4000);
  }
});

watch(visibleUserCount, (nv) => {
  if ((nv > 1 || (nv === 1 && iAmOwner.value)) && !forceReady.value) {
    setTimeout(() => {
      sidebarStore.setForceReady(true);
    }, 3000);
  }
});

watch(focused, (isFocused) => {
  if (isFocused) {
    sidebarStore.checkForBar();
  }
});

watchDebounced(
  posCallTs,
  () => {
    sidebarStore.checkForBar();
    cleanUpStyles(mainContentContainer.value);
    cleanUpStyles(extraContentContainer.value);
  },
  { debounce: 3000 },
);

// Animate the panes

// Animate panes
function openSidebar() {
  cleanUpStyles(mainContentContainer.value);
  return animationGuard(
    () => {
      if (!sidebar.value) {
        return false;
      }
      sidebarStore.setIsOpening(true);
      sidebarStore.setIsOpen(true);
      changeBodyClass({ add: 'sidebar-opening', remove: 'sidebar-closed' });

      return new Promise((resolve) => {
        ev &&
          sidebar.value.addEventListener(
            ev,
            () => {
              sidebarStore.setIsOpening(false);
              sidebarStore.setIsOpen(true);
              changeBodyClass({ add: 'sidebar-open', remove: 'sidebar-opening' });
              sidebarOpenedThisMeeting.value = true;
              resolve();
            },
            { once: true },
          );
      });
    },
    () => {
      removeBodyClass(['sidebar-opening', 'sidebar-closing']);
      changeBodyClass({ add: 'sidebar-open', remove: 'sidebar-closed' });
      sidebarOpenedThisMeeting.value = true;
      sidebarStore.setIsOpen(true);
      return true;
    },
  );
}

function closeSidebar() {
  return animationGuard(
    () => {
      if (!sidebar.value || !isOpen.value) {
        return false;
      }
      sidebarStore.setIsClosing(true);
      changeBodyClass({ add: 'sidebar-closing', remove: 'sidebar-open' });
      return new Promise((resolve) => {
        ev &&
          sidebar.value.addEventListener(
            ev,
            () => {
              sidebarStore.setIsClosing(false);
              sidebarStore.setIsOpen(false);
              changeBodyClass({ add: 'sidebar-closed', remove: ['sidebar-closing', 'sidebar-open'] });
              resolve();
              cleanUpStyles(mainContentContainer.value);
            },
            { once: true },
          );
      });
    },
    () => {
      removeBodyClass(['sidebar-opening', 'sidebar-closing']);
      changeBodyClass({ add: 'sidebar-closed', remove: 'sidebar-open' });
      sidebarStore.setIsOpen(false);
      cleanUpStyles(mainContentContainer.value);
      return true;
    },
  );
}

function mainEnter(el, done) {
  paneEnter(el, done, 'main');
}

function extraEnter(el, done) {
  paneEnter(el, done, 'extra');
}
function mainLeave(el, done) {
  paneLeave(el, done, 'main');
}

function extraLeave(el, done) {
  paneLeave(el, done, 'extra');
}

function paneEnter(el, done, type) {
  cleanUpStyles(el);
  if (!currentPane.value) {
    done();
    return;
  }

  if (type === 'main' && !isOpen.value) {
    // We are opening
    openSidebar().then(done);
  } else if (type === 'extra' && !previousExtra.value.name) {
    done();
  } else {
    // We are changing
    const direction = type === 'main' ? panelDirection.value : extraPanelDirection.value;
    el.style.transform = direction === 'toRight' ? 'translate(-100%)' : 'translate(100%)';
    let initVal = direction === 'toRight' ? '-100%' : '100%';
    animationGuard(
      () => {
        sidebarStore.setIsEntering(true);
        return gsap.fromTo(el, { x: initVal }, { x: '0%', duration: 0.6, ease: 'power2.inOut' }).then(() => {
          sidebarStore.setIsEntering(false);
          cleanUpStyles(el);
          done();
        });
      },
      () => {
        gsap.set(el, { x: '0%' });
        cleanUpStyles(el);
        sidebarStore.setIsEntering(false);
        done();
      },
    );
  }

  focusing.value = false;
}

function cleanUpStyles(el) {
  const $main = document.getElementById('mainSidebar');

  if (el?.style?.display === 'none') {
    el.style.display = '';
  }
  if ($main?.style?.display === 'none') {
    $main.style.display = '';
  }
}

function afterPaneEnter(el) {
  focusing.value = true;
  const focusMe = el.querySelector('.focusPlease');
  if (focusMe) {
    focusMe.focus();
  }
}

function paneLeave(el, done, type) {
  if (type === 'main' && !currentPane.value) {
    closeSidebar();
    done();
  } else if (type === 'extra' && !extra.value) {
    _.delay(done, 500);
  } else {
    const direction = type === 'main' ? panelDirection.value : extraPanelDirection.value;
    let finalVal = direction === 'toLeft' ? '-100%' : '100%';

    animationGuard(
      () => {
        sidebarStore.setIsLeaving(true);
        return gsap.fromTo(el, { x: '0%' }, { x: finalVal, duration: 0.6, ease: 'power2.inOut' }).then(() => {
          sidebarStore.setIsLeaving(false);
          done();
        });
      },
      () => {
        gsap.set(el, { x: finalVal });
        sidebarStore.setIsLeaving(false);
        done();
      },
    );
  }
}

function requestFinish() {
  messagesStore
    .interrupt('sureYouWantToFinishMeeting')
    .then(() => {
      $ee.emit('nM:meeting:finish');
    })
    .catch(() => {});
}
</script>
<style lang="scss" scoped>
.bixeSidebarContainer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  height: calc(100vh - #{$header-height});
  height: calc(var(--vh, 1vh) * 100 - #{$header-height});
  top: $header-height;
  &.sidebarHidden,
  &.fullscreenHide {
    display: none;
  }
}
.sidebar {
  width: rem($sidebar-width);
  right: 0;
  bottom: 0;
  top: 0;
  position: absolute;
  border-left: 1px solid var(--c__edges);
  transform: translateX(100%);
  transition: transform $sidebar-duration $sidebar-ease-general, bottom $sidebar-duration $sidebar-ease-general;
  z-index: 200;
  overflow: hidden;

  &.hasNav {
    bottom: rem($log-height);
  }

  .sidebar-open &,
  .sidebar-opening & {
    transform: translateX(0%);
  }

  .sidebar-opening & {
    transition: transform $sidebar-duration $sidebar-ease-enter;
  }

  .sidebar-closing & {
    transition: transform $sidebar-duration $sidebar-ease-exit;
  }
}
section.main,
section.extra {
  transition: all 0.6s $sidebar-ease-general;
  position: absolute;
  left: 0;
  right: 0;
  overflow-x: hidden;
}
.close {
  position: absolute;
  top: rem(18px);
  right: rem(18px);
  height: rem(18px);
  width: rem(18px);
  padding: 0;
  z-index: 2;
  :deep(svg) {
    width: 100%;
    display: block;
    * > {
      vector-effect: non-scaling-stroke;
    }
    .stroke {
      stroke-width: 1.6px;
    }
  }
  @include triggered {
    color: var(--c__accent);
  }
}

:deep(.sidebarTitle) {
  font-family: $stack-nowhere;
  text-transform: lowercase;
  @include type-size('large');
}
.mainContent,
.extraContent {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  z-index: 1;
}
section.extra {
  height: 0;
  bottom: 0;
  background: var(--c__sidebar-bg);
  container: --container-sidebar-extra / size;
}
.divider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--c__edges);
  z-index: 99;
}

section.main {
  top: 0;
  height: 100%;
  background: var(--c__sidebar-bg);

  container: --container-sidebar-main / size;
}

.split section.main,
.split section.extra {
  height: 50%;
}

.sidebarNav {
  z-index: 211;
}

.navBar-item {
  transition: all 0.3s cubic-bezier(0.5, 0, 0.5, 1);
}

.navBar-enter-active {
  transition: all 0.3s cubic-bezier(0, 1, 1, 1);
}

.navBar-leave-active {
  transition: all 0.3s cubic-bezier(0.5, 0, 1, 0.5);
}

.navBar-leave-to,
.navBar-enter-from {
  transform: translateY(100%);
}
</style>
